import React, { useCallback } from 'react';
import {
  getTherapeuticAreasAction,
  getDrugsAction,
  getPathologiesAction,
  getCentersAction,
  getContactsAction,
  getContactAction,
  getCompaniesAction,
} from '../../actions/contentActions';
import { getCongressAction } from '../../actions/userActions';
import ContentList, { ContentDetail } from './ContentList';
import styles from '../../styles/consult.module.scss';

function Field({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div>
      <label>{label}</label>
      <div className={styles.value}>{children}</div>
    </div>
  );
}

export function AiresList() {
  return (
    <ContentList
      title="Aires thérapeutiques"
      listSelector={(s) => s.content.therapeuticAreas}
      fetchAction={getTherapeuticAreasAction}
      basePath="/majee/aires"
      getLabel={(i) => i.name || i.title || '—'}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => i.name || i.title || '',
          render: (i) => i.name || i.title || '—',
        },
      ]}
    />
  );
}

export function ProduitsList() {
  return (
    <ContentList
      title="Produits"
      listSelector={(s) => s.content.drugs}
      fetchAction={getDrugsAction}
      basePath="/majee/produits"
      getLabel={(i) => i.name || '—'}
      getSubLabel={(i) => i.company?.name || i.laboratory || ''}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => i.name || '',
          render: (i) => i.name || '—',
        },
        {
          key: 'lab',
          label: 'Laboratoire',
          sortValue: (i) => i.company?.name || i.laboratory || '',
          render: (i) => i.company?.name || i.laboratory || '—',
        },
      ]}
    />
  );
}

export function PathologiesList() {
  return (
    <ContentList
      title="Pathologies"
      listSelector={(s) => s.content.pathologies}
      fetchAction={getPathologiesAction}
      basePath="/majee/pathologies"
      getLabel={(i) => i.name || i.title || '—'}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => i.name || i.title || '',
          render: (i) => (
            <span className={styles.nameWithColor}>
              <span
                className={styles.colorDot}
                style={{ backgroundColor: i?.color || '#CFD0DB' }}
              />
              {i.name || i.title || '—'}
            </span>
          ),
        },
      ]}
    />
  );
}

export function CentresList() {
  return (
    <ContentList
      title="Centres & services"
      listSelector={(s) => s.content.centers}
      fetchAction={getCentersAction}
      basePath="/majee/centres"
      getLabel={(i) => i.name || i.establishment || '—'}
      getSubLabel={(i) => i.location?.city || i.city || ''}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => i.name || i.establishment || '',
          render: (i) => i.name || i.establishment || '—',
        },
        {
          key: 'city',
          label: 'Ville',
          sortValue: (i) => i.location?.city || i.city || '',
          render: (i) => i.location?.city || i.city || '—',
        },
      ]}
    />
  );
}

export function ContactsList() {
  return (
    <ContentList
      title="Contacts médicaux"
      listSelector={(s) => s.content.contacts}
      fetchAction={getContactsAction}
      basePath="/majee/contacts"
      getLabel={(i) => `${i.firstName || ''} ${i.lastName || ''}`.trim() || i.name || '—'}
      getSubLabel={(i) => i.specialty || i.speciality || ''}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => `${i.firstName || ''} ${i.lastName || ''}`.trim() || i.name || '',
          render: (i) => `${i.firstName || ''} ${i.lastName || ''}`.trim() || '—',
        },
        {
          key: 'spec',
          label: 'Spécialité',
          sortValue: (i) => i.specialty || i.speciality || '',
          render: (i) => i.specialty || i.speciality || '—',
        },
      ]}
    />
  );
}

export function ContactsDetail() {
  return (
    <ContentDetail
      title={(item) => `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || 'Contact'}
      fetchAction={getContactAction}
      itemSelector={(s) => s.content.contact}
      backPath="/majee/contacts"
    >
      {(item) => (
        <div className={styles.panel}>
          <Field label="Email">{item?.email}</Field>
          <Field label="Téléphone">{item?.phone || item?.mobile}</Field>
          <Field label="Spécialité">{item?.specialty || item?.speciality}</Field>
          <Field label="Centre">{item?.center?.name}</Field>
        </div>
      )}
    </ContentDetail>
  );
}

export function CongresList() {
  const fetch = useCallback((dispatch) => getCongressAction(dispatch), []);
  return (
    <ContentList
      title="Congrès"
      listSelector={(s) => s.user.congress}
      fetchAction={fetch}
      basePath="/majee/congres"
      getLabel={(i) => i.title || i.name || '—'}
      getSubLabel={(i) => i.city || ''}
      columns={[
        {
          key: 'title',
          label: 'Titre',
          sortValue: (i) => i.title || i.name || '',
          render: (i) => i.title || i.name || '—',
        },
        {
          key: 'city',
          label: 'Ville',
          sortValue: (i) => i.city || '',
          render: (i) => i.city || '—',
        },
        {
          key: 'dates',
          label: 'Dates',
          sortType: 'date',
          sortValue: (i) => i.startDate || i.endDate || '',
          desktop: true,
          render: (i) => [i.startDate, i.endDate].filter(Boolean).join(' → ') || '—',
        },
      ]}
    />
  );
}

export function ConcurrentsList() {
  return (
    <ContentList
      title="Fiches concurrents"
      listSelector={(s) => s.content.companies}
      fetchAction={getCompaniesAction}
      basePath="/majee/concurrents"
      getLabel={(i) => i.name || '—'}
      getSubLabel={(i) => i.type || ''}
      columns={[
        {
          key: 'name',
          label: 'Nom',
          sortValue: (i) => i.name || '',
          render: (i) => i.name || '—',
        },
        {
          key: 'type',
          label: 'Type',
          sortValue: (i) => i.type || '',
          render: (i) => i.type || '—',
        },
      ]}
    />
  );
}
